import { useMapMagicPlaces, visibleOnMap } from '@features/magic/useMapMagicPlaces'
import { useLocalities } from '@features/places/usePlaces'
import { useI18n } from '@i18n/I18nProvider'
import { Icon } from '@ui/Icon'
import { ErrorState, LoadingState } from '@ui/States'
import { type ComponentProps, type ComponentType, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useSearch } from 'wouter'
import { magicPlaceFromSearch, mapHrefForStage, placeFromSearch, stageFromSearch } from './focus'
import {
	magicPlacesToGeoJson,
	poisToGeoJson,
	stageEndsToGeoJson,
	stagesToGeoJson,
	treesToGeoJson,
	variantsToGeoJson,
} from './geo'
import { LegendButton } from './LegendButton'
import type { MapSelection, MapView } from './MapView'
import { SelectionSheet } from './SelectionSheet'
import { StageDetailsButton } from './StageDetails'
import { StagePicker } from './StagePicker'
import { countInStage, inStage, sortStages, stagePosition } from './stage-route'
import { useMapData } from './useMapData'

type CanvasProps = Omit<ComponentProps<typeof MapView>, 'createMap'>
const LazyMapCanvas = lazy(() => import('./MapCanvas'))

// Portada: el mapa del Camino a pantalla completa, entre la cabecera y la barra inferior.
// `Canvas` se inyecta en los tests (MapLibre necesita WebGL). Con `active` a false (se está en
// otra pantalla, ver KeptMap) sigue montado pero invisible e inerte, y no atiende a la URL.
export const MapPage = ({
	Canvas = LazyMapCanvas,
	active = true,
}: {
	Canvas?: ComponentType<CanvasProps>
	active?: boolean
}) => {
	const { t } = useI18n()
	const { trees, stages, pois } = useMapData()
	// Lugares mágicos: los aprobados y, con sesión de gestor, también los recibidos.
	const magicPlaces = useMapMagicPlaces()
	// Las variantes propuestas salen de las localidades. Si no cargan, el mapa sigue sin ellas.
	const localities = useLocalities()
	// Mientras está oculto, la URL es la de otra pantalla: se sigue con la última del mapa.
	const liveSearch = useSearch()
	const lastSearch = useRef(liveSearch)
	if (active) lastSearch.current = liveSearch
	const search = lastSearch.current
	const [, navigate] = useLocation()
	// `/?lugar=<id>` (o `/?lugar-magico=<id>`) abre el mapa centrado en ese lugar y con su hoja abierta.
	const placeId = placeFromSearch(search)
	const magicId = magicPlaceFromSearch(search)
	const [selection, setSelection] = useState<MapSelection | null>(
		placeId ? { kind: 'poi', id: placeId } : magicId ? { kind: 'magic', id: magicId } : null
	)
	// Al irse a otra pantalla se cierra la hoja; al volver con un lugar en la URL, se abre la suya.
	useEffect(() => {
		if (!active) setSelection(null)
		else if (placeId) setSelection({ kind: 'poi', id: placeId })
		else if (magicId) setSelection({ kind: 'magic', id: magicId })
	}, [active, placeId, magicId])
	// `/?etapa=<id>`: la etapa elegida vive en la URL (se puede enlazar y sobrevive a recargar).
	// Cambiar de etapa reemplaza la entrada del historial para no llenarlo al ir de una en una.
	const sortedStages = useMemo(() => sortStages(stages.data?.items ?? []), [stages.data])
	const position = stagePosition(sortedStages, stageFromSearch(search))
	const stage = position?.stage ?? null
	const chooseStage = (id: string | null) => {
		setSelection(null)
		navigate(id ? mapHrefForStage(id) : '/', { replace: true })
	}
	const select = (next: MapSelection | null) => (next?.kind === 'stage' ? chooseStage(next.id) : setSelection(next))
	// Los retirados solo se pintan si son el lugar mágico que se está mirando.
	const shownMagicId = selection?.kind === 'magic' ? selection.id : magicId
	const visibleMagicPlaces = useMemo(() => visibleOnMap(magicPlaces, shownMagicId), [magicPlaces, shownMagicId])
	// Oculto, sin punto: así, al volver al mismo lugar, el mapa se centra otra vez en él.
	const focus = useMemo(
		() =>
			(active &&
				(pois.data?.items.find((poi) => poi.id === placeId)?.location ??
					magicPlaces.find((place) => place.id === magicId)?.location)) ||
			null,
		[active, pois.data, placeId, magicPlaces, magicId]
	)

	// Con una etapa elegida, solo sus árboles y lugares (los de las demás no distraen). Se filtra
	// en los datos para que los grupos del mapa cuenten solo lo de la etapa.
	const stageId = stage?.id ?? null
	const geo = useMemo(
		() => ({
			trees: treesToGeoJson(inStage(trees.data?.items ?? [], stageId)),
			stages: stagesToGeoJson(stages.data?.items ?? []),
			// Los lugares mágicos no son de una etapa: se ven siempre.
			pois: {
				type: 'FeatureCollection' as const,
				features: [
					...poisToGeoJson(inStage(pois.data?.items ?? [], stageId)).features,
					...magicPlacesToGeoJson(visibleMagicPlaces).features,
				],
			},
		}),
		[trees.data, stages.data, pois.data, stageId, visibleMagicPlaces]
	)
	const stageEnds = useMemo(() => stageEndsToGeoJson(stage), [stage])
	const variants = useMemo(() => variantsToGeoJson(localities.data?.items ?? []), [localities.data])

	const failed = trees.error ?? stages.error ?? pois.error
	const retry = () => Promise.all([trees.refetch(), stages.refetch(), pois.refetch()])

	return (
		<main
			inert={!active}
			aria-hidden={!active || undefined}
			className={`${active ? '' : 'pointer-events-none opacity-0 '}fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top))] bottom-[calc(4rem+env(safe-area-inset-bottom))]`}
		>
			{failed ? (
				<ErrorState error={failed} onRetry={retry} />
			) : (
				<Suspense fallback={<LoadingState shape="map" />}>
					<Canvas
						trees={geo.trees}
						stages={geo.stages}
						pois={geo.pois}
						stageEnds={stageEnds}
						variants={variants}
						selectedTreeCode={selection?.kind === 'tree' ? selection.code : null}
						selectedStageId={stage?.id ?? null}
						onSelect={select}
						focus={focus}
					/>
				</Suspense>
			)}
			{/* Los controles y sus hojas (que van en un portal) solo con el mapa a la vista. */}
			{active && (
				<>
					<StagePicker stages={sortedStages} selectedId={stage?.id ?? null} onChoose={chooseStage}>
						{position && (
							<StageDetailsButton
								position={position}
								trees={countInStage(trees.data?.items ?? [], position.stage.id)}
								pois={countInStage(pois.data?.items ?? [], position.stage.id)}
								onChoose={chooseStage}
							/>
						)}
					</StagePicker>
					{!selection && (
						<div className="absolute inset-x-2 bottom-2 z-10 mx-auto flex max-w-xl flex-col items-end gap-3">
							{/* Proponer un lugar mágico: en el mapa, que es la primera pantalla, para que se vea. */}
							<Link
								href="/lugar-magico"
								className="mr-2 inline-flex min-h-12 items-center gap-2 rounded-full bg-white py-3 pr-5 pl-4 text-base font-bold text-forest-800 shadow-xl active:bg-earth-100"
							>
								<Icon name="sparkles" className="size-6" />
								{t('map.magicPlace')}
							</Link>
							{/* Botón flotante del mockup: ocre, abajo a la derecha, siempre con texto. */}
							<Link
								href="/escanear"
								className="mr-2 mb-2 inline-flex min-h-12 items-center gap-2 rounded-full bg-ochre py-3 pr-5 pl-4 text-base font-bold text-white shadow-xl active:brightness-90"
							>
								<Icon name="qr" className="size-6" />
								{t('scan.open')}
							</Link>
						</div>
					)}
					<LegendButton withStageEnds={stage !== null} />
					<SelectionSheet
						selection={selection}
						trees={trees.data?.items ?? []}
						pois={pois.data?.items ?? []}
						magicPlaces={visibleMagicPlaces}
						localities={localities.data?.items ?? []}
						onClose={() => setSelection(null)}
					/>
				</>
			)}
		</main>
	)
}
